import React, { useState, useRef, useEffect } from "react";
import { RootState } from "../../store/store";
import { useDispatch, useSelector } from 'react-redux';
import { setModel } from '../../store/slices/modelSlice';
import  useWebSocketContext  from '../hooks/useWebSocketContext'; 
import { useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import RegenAILogo from '../../assets/regenai-logo.png';
import { isThinkingCapable, MODEL_CAPABILITY_INDICATORS } from '../../utils/modelCapabilities';

// Helper function to check if a model is disabled (local models not available)
const isModelDisabled = (modelTitle: string): boolean => {
  return modelTitle === "llama3.3:70b" || modelTitle === "deepseek-r1:32b";
};

interface Model {
  id: number;
  title: string;
  provider: string;
  desc: string;
  icon: React.ReactNode;
}

interface GroupedModels {
  [key: string]: Model[];
}

function Header() {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedModel = useSelector((state: RootState) => state.model.selectedModel);
  const theme = useTheme();

const models = [
  {
    id: 1,
    title: "gpt-5-mini",
    provider: "OpenAI",
    desc: "Great for everyday tasks",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill={theme.palette.text.secondary}
        viewBox="0 0 24 24"
        width="20"
        height="20"
      >
        <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.0314 3.8065L12.5 8.3397l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.6813zm2.0107-3.0231l-.142-.0852-4.7782-2.7818a.7712.7712 0 0 0-.7806 0L9.409 9.2907V6.9583a.0804.0804 0 0 1 .0332-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.071.071 0 0 1-.038-.052V6.0672a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
      </svg>
    ),
  },
  {
    id: 2,
    title: "gpt-5",
    provider: "OpenAI",
    desc: "Advanced model for complex tasks",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill={theme.palette.text.secondary}
        viewBox="0 0 24 24"
        width="20"
        height="20"
      >
        <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.0314 3.8065L12.5 8.3397l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.6813zm2.0107-3.0231l-.142-.0852-4.7782-2.7818a.7712.7712 0 0 0-.7806 0L9.409 9.2907V6.9583a.0804.0804 0 0 1 .0332-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.071.071 0 0 1-.038-.052V6.0672a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
      </svg>
    ),
  },
  {
    id: 3,
    title: "gpt-5-nano",
    provider: "OpenAI",
    desc: "Lightweight model for fast responses",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill={theme.palette.text.secondary}
        viewBox="0 0 24 24"
        width="20"
        height="20"
      >
        <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.0314 3.8065L12.5 8.3397l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.6813zm2.0107-3.0231l-.142-.0852-4.7782-2.7818a.7712.7712 0 0 0-.7806 0L9.409 9.2907V6.9583a.0804.0804 0 0 1 .0332-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.071.071 0 0 1-.038-.052V6.0672a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
      </svg>
    ),
  },
  {
    id: 4,
    title: "gemini-flash-2.5",
    provider: "Google",
    desc: "Great for everyday tasks",
    icon: (<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path d="M16 8.016A8.522 8.522 0 008.016 16h-.032A8.521 8.521 0 000 8.016v-.032A8.521 8.521 0 007.984 0h.032A8.522 8.522 0 0016 7.984v.032z" fill="url(#prefix__paint0_radial_980_20147)"/><defs><radialGradient id="prefix__paint0_radial_980_20147" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(16.1326 5.4553 -43.70045 129.2322 1.588 6.503)"><stop offset=".067" stopColor="#9168C0"/><stop offset=".343" stopColor="#5684D1"/><stop offset=".672" stopColor="#1BA1E3"/></radialGradient></defs></svg>)
  },
  {
    id: 5,
    title: "gemini-pro-2.5",
    provider: "Google",
    desc: "Advanced model for complex tasks",
    icon: (<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16"><path d="M16 8.016A8.522 8.522 0 008.016 16h-.032A8.521 8.521 0 000 8.016v-.032A8.521 8.521 0 007.984 0h.032A8.522 8.522 0 0016 7.984v.032z" fill="url(#prefix__paint0_radial_980_20147)"/><defs><radialGradient id="prefix__paint0_radial_980_20147" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(16.1326 5.4553 -43.70045 129.2322 1.588 6.503)"><stop offset=".067" stopColor="#9168C0"/><stop offset=".343" stopColor="#5684D1"/><stop offset=".672" stopColor="#1BA1E3"/></radialGradient></defs></svg>)
  },
  {
    id: 6,
    title: "deepseek-v3",
    provider: "DeepSeek",
    desc: "Great for everyday tasks",
    icon: (<svg shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 512 509.64"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill="#4D6BFE" fillRule="nonzero" d="M440.898 139.167c-4.001-1.961-5.723 1.776-8.062 3.673-.801.612-1.479 1.407-2.154 2.141-5.848 6.246-12.681 10.349-21.607 9.859-13.048-.734-24.192 3.368-34.04 13.348-2.093-12.307-9.048-19.658-19.635-24.37-5.54-2.449-11.141-4.9-15.02-10.227-2.708-3.795-3.447-8.021-4.801-12.185-.861-2.509-1.725-5.082-4.618-5.512-3.139-.49-4.372 2.142-5.601 4.349-4.925 9.002-6.833 18.921-6.647 28.962.432 22.597 9.972 40.597 28.932 53.397 2.154 1.47 2.707 2.939 2.032 5.082-1.293 4.41-2.832 8.695-4.186 13.105-.862 2.817-2.157 3.429-5.172 2.205-10.402-4.346-19.391-10.778-27.332-18.553-13.481-13.044-25.668-27.434-40.873-38.702a177.614 177.614 0 00-10.834-7.409c-15.512-15.063 2.032-27.434 6.094-28.902 4.247-1.532 1.478-6.797-12.251-6.736-13.727.061-26.285 4.653-42.288 10.777-2.34.92-4.801 1.593-7.326 2.142-14.527-2.756-29.608-3.368-45.367-1.593-29.671 3.305-53.368 17.329-70.788 41.272-20.928 28.785-25.854 61.482-19.821 95.59 6.34 35.943 24.683 65.704 52.876 88.974 29.239 24.123 62.911 35.943 101.32 33.677 23.329-1.346 49.307-4.468 78.607-29.27 7.387 3.673 15.142 5.144 28.008 6.246 9.911.92 19.452-.49 26.839-2.019 11.573-2.449 10.773-13.166 6.586-15.124-33.915-15.797-26.47-9.368-33.24-14.573 17.235-20.39 43.213-41.577 53.369-110.222.8-5.448.121-8.877 0-13.287-.061-2.692.553-3.734 3.632-4.041 8.494-.981 16.742-3.305 24.314-7.471 21.975-12.002 30.84-31.719 32.933-55.355.307-3.612-.061-7.348-3.879-9.245v-.003zM249.4 351.89c-32.872-25.838-48.814-34.352-55.4-33.984-6.155.368-5.048 7.41-3.694 12.002 1.415 4.532 3.264 7.654 5.848 11.634 1.785 2.634 3.017 6.551-1.784 9.493-10.587 6.55-28.993-2.205-29.856-2.635-21.421-12.614-39.334-29.269-51.954-52.047-12.187-21.924-19.267-45.435-20.435-70.542-.308-6.061 1.478-8.207 7.509-9.307 7.94-1.471 16.127-1.778 24.068-.615 33.547 4.9 62.108 19.902 86.054 43.66 13.666 13.531 24.007 29.699 34.658 45.496 11.326 16.778 23.514 32.761 39.026 45.865 5.479 4.592 9.848 8.083 14.035 10.656-12.62 1.407-33.673 1.714-48.075-9.676zm15.899-102.519c.521-2.111 2.421-3.658 4.722-3.658a4.74 4.74 0 011.661.305c.678.246 1.293.614 1.786 1.163.861.859 1.354 2.083 1.354 3.368 0 2.695-2.154 4.837-4.862 4.837a4.748 4.748 0 01-4.738-4.034 5.01 5.01 0 01.077-1.981zm47.208 26.915c-2.606.996-5.2 1.778-7.707 1.88-4.679.244-9.787-1.654-12.556-3.981-4.308-3.612-7.386-5.631-8.679-11.941-.554-2.695-.247-6.858.246-9.246 1.108-5.144-.124-8.451-3.754-11.451-2.954-2.449-6.711-3.122-10.834-3.122-1.539 0-2.954-.673-4.001-1.224-1.724-.856-3.139-3-1.785-5.634.432-.856 2.525-2.939 3.018-3.305 5.6-3.185 12.065-2.144 18.034.244 5.54 2.266 9.727 6.429 15.759 12.307 6.155 7.102 7.263 9.063 10.773 14.39 2.771 4.163 5.294 8.451 7.018 13.348.877 2.561.071 4.74-2.341 6.277-.981.625-2.109 1.044-3.191 1.458z"/></svg>)
  },
  {
    id: 11,
    title: "claude-4-sonnet",
    provider: "Anthropic",
    desc: "Great for coding tasks",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        x="0px"
        y="0px"
        width="50"
        height="50"
        viewBox="0 0 48 48"
      >
        <path
          fill="#d19b75"
          d="M40,6H8C6.895,6,6,6.895,6,8v32c0,1.105,0.895,2,2,2h32c1.105,0,2-0.895,2-2V8	C42,6.895,41.105,6,40,6z"
        ></path>
        <path
          fill="#252525"
          d="M22.197,14.234h-4.404L10.037,33.67c0-0.096,4.452,0,4.452,0l1.484-4.069h8.234l1.58,4.069h4.261	L22.197,14.234z M17.362,26.059l2.729-6.894l2.633,6.894C22.723,26.059,17.266,26.059,17.362,26.059z"
        ></path>
        <path
          fill="#252525"
          d="M25.963,14.234L33.59,33.67h4.356l-7.803-19.436C30.144,14.234,25.963,14.186,25.963,14.234z"
        ></path>
      </svg>
    ),
  },
  {
    id: 10,
    title: "glm-4.5",
    provider: "Z.ai",
    desc: "Advanced reasoning model",
    icon: (
      <div 
        style={{
          width: '25px',
          height: '25px',
          borderRadius: '10%',
          backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <img
          src="https://chat.z.ai/static/logoLight.png"
          alt="Z.ai logo"
          width="20"
          height="20"
          style={{ borderRadius: '1%' }}
        />
      </div>
    ),
  },
  {
    id: 7,
    title: "llama3.3:70b",
    provider: "Locally Hosted",
    desc: "Great for everyday tasks",
    icon: (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        x="0px"
        y="0px"
        width="50"
        height="50"
        viewBox="0 0 48 48"
      >
        <path
          fill="#0081fb"
          d="M47,29.36l-2.193,1.663L42.62,29.5c0-0.16,0-0.33-0.01-0.5c0-0.16,0-0.33-0.01-0.5	c-0.14-3.94-1.14-8.16-3.14-11.25c-1.54-2.37-3.51-3.5-5.71-3.5c-2.31,0-4.19,1.38-6.27,4.38c-0.06,0.09-0.13,0.18-0.19,0.28	c-0.04,0.05-0.07,0.1-0.11,0.16c-0.1,0.15-0.2,0.3-0.3,0.46c-0.9,1.4-1.84,3.03-2.86,4.83c-0.09,0.17-0.19,0.34-0.28,0.51	c-0.03,0.04-0.06,0.09-0.08,0.13l-0.21,0.37l-1.24,2.19c-2.91,5.15-3.65,6.33-5.1,8.26C14.56,38.71,12.38,40,9.51,40	c-3.4,0-5.56-1.47-6.89-3.69C1.53,34.51,1,32.14,1,29.44l4.97,0.17c0,1.76,0.38,3.1,0.89,3.92C7.52,34.59,8.49,35,9.5,35	c1.29,0,2.49-0.27,4.77-3.43c1.83-2.53,3.99-6.07,5.44-8.3l1.37-2.09l0.29-0.46l0.3-0.45l0.5-0.77c0.76-1.16,1.58-2.39,2.46-3.57	c0.1-0.14,0.2-0.28,0.31-0.42c0.1-0.14,0.21-0.28,0.31-0.41c0.9-1.15,1.85-2.22,2.87-3.1c1.85-1.61,3.84-2.5,5.85-2.5	c3.37,0,6.58,1.95,9.04,5.61c2.51,3.74,3.82,8.4,3.97,13.25c0.01,0.16,0.01,0.33,0.01,0.5C47,29.03,47,29.19,47,29.36z"
        ></path>
        <linearGradient
          id="wSMw7pqi7WIWHewz2_TZXa_PvvcWRWxRKSR_gr1"
          x1="42.304"
          x2="13.533"
          y1="24.75"
          y2="24.75"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#0081fb"></stop>
          <stop offset=".995" stopColor="#0064e1"></stop>
        </linearGradient>
        <path
          fill="url(#wSMw7pqi7WIWHewz2_TZXa_PvvcWRWxRKSR_gr1)"
          d="M4.918,15.456	C7.195,11.951,10.483,9.5,14.253,9.5c2.184,0,4.354,0.645,6.621,2.493c2.479,2.02,5.122,5.346,8.419,10.828l1.182,1.967	c2.854,4.746,4.477,7.187,5.428,8.339C37.125,34.606,37.888,35,39,35c2.82,0,3.617-2.54,3.617-5.501L47,29.362	c0,3.095-0.611,5.369-1.651,7.165C44.345,38.264,42.387,40,39.093,40c-2.048,0-3.862-0.444-5.868-2.333	c-1.542-1.45-3.345-4.026-4.732-6.341l-4.126-6.879c-2.07-3.452-3.969-6.027-5.068-7.192c-1.182-1.254-2.642-2.754-5.067-2.754	c-1.963,0-3.689,1.362-5.084,3.465L4.918,15.456z"
        ></path>
        <linearGradient
          id="wSMw7pqi7WIWHewz2_TZXb_PvvcWRWxRKSR_gr2"
          x1="7.635"
          x2="7.635"
          y1="32.87"
          y2="13.012"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#0081fb"></stop>
          <stop offset=".995" stopColor="#0064e1"></stop>
        </linearGradient>
        <path
          fill="url(#wSMw7pqi7WIWHewz2_TZXb_PvvcWRWxRKSR_gr2)"
          d="M14.25,14.5	c-1.959,0-3.683,1.362-5.075,3.465C7.206,20.937,6,25.363,6,29.614c0,1.753-0.003,3.072,0.5,3.886l-3.84,2.813	C1.574,34.507,1,32.2,1,29.5c0-4.91,1.355-10.091,3.918-14.044C7.192,11.951,10.507,9.5,14.27,9.5L14.25,14.5z"
        ></path>
        <path
          d="M21.67,20.27l-0.3,0.45l-0.29,0.46c0.71,1.03,1.52,2.27,2.37,3.69l0.21-0.37c0.02-0.04,0.05-0.09,0.08-0.13 c0.09-0.17,0.19-0.34,0.28-0.51C23.19,22.5,22.39,21.29,21.67,20.27z M24.94,15.51c-0.11,0.14-0.21,0.28-0.31,0.42 c0.73,0.91,1.47,1.94,2.25,3.1c0.1-0.16,0.2-0.31,0.3-0.46c0.04-0.06,0.07-0.11,0.11-0.16c0.06-0.1,0.13-0.19,0.19-0.28 c-0.76-1.12-1.5-2.13-2.23-3.03C25.15,15.23,25.04,15.37,24.94,15.51z"
          opacity=".05"
        ></path>
        <path
          d="M21.67,20.27l-0.3,0.45c0.71,1.02,1.51,2.24,2.37,3.65c0.09-0.17,0.19-0.34,0.28-0.51C23.19,22.5,22.39,21.29,21.67,20.27 z M24.63,15.93c0.73,0.91,1.47,1.94,2.25,3.1c0.1-0.16,0.2-0.31,0.3-0.46c-0.77-1.14-1.52-2.16-2.24-3.06 C24.83,15.65,24.73,15.79,24.63,15.93z"
          opacity=".07"
        ></path>
      </svg>
    ),
  },
  {
    id: 8,
    title: "RegenAI-finetuned",
    provider: "Custom",
    desc: "Great for everyday tasks",
    icon: (
      <img
        src={RegenAILogo}
        alt="icon"
      />
    ),
  },
  {
    id: 9,
    title: "deepseek-r1:32b",
    provider: "Locally Hosted",
    desc: "Reasoning model",
    icon: (<svg shapeRendering="geometricPrecision" textRendering="geometricPrecision" imageRendering="optimizeQuality" fillRule="evenodd" clipRule="evenodd" viewBox="0 0 512 509.64"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill="#4D6BFE" fillRule="nonzero" d="M440.898 139.167c-4.001-1.961-5.723 1.776-8.062 3.673-.801.612-1.479 1.407-2.154 2.141-5.848 6.246-12.681 10.349-21.607 9.859-13.048-.734-24.192 3.368-34.04 13.348-2.093-12.307-9.048-19.658-19.635-24.37-5.54-2.449-11.141-4.9-15.02-10.227-2.708-3.795-3.447-8.021-4.801-12.185-.861-2.509-1.725-5.082-4.618-5.512-3.139-.49-4.372 2.142-5.601 4.349-4.925 9.002-6.833 18.921-6.647 28.962.432 22.597 9.972 40.597 28.932 53.397 2.154 1.47 2.707 2.939 2.032 5.082-1.293 4.41-2.832 8.695-4.186 13.105-.862 2.817-2.157 3.429-5.172 2.205-10.402-4.346-19.391-10.778-27.332-18.553-13.481-13.044-25.668-27.434-40.873-38.702a177.614 177.614 0 00-10.834-7.409c-15.512-15.063 2.032-27.434 6.094-28.902 4.247-1.532 1.478-6.797-12.251-6.736-13.727.061-26.285 4.653-42.288 10.777-2.34.92-4.801 1.593-7.326 2.142-14.527-2.756-29.608-3.368-45.367-1.593-29.671 3.305-53.368 17.329-70.788 41.272-20.928 28.785-25.854 61.482-19.821 95.59 6.34 35.943 24.683 65.704 52.876 88.974 29.239 24.123 62.911 35.943 101.32 33.677 23.329-1.346 49.307-4.468 78.607-29.27 7.387 3.673 15.142 5.144 28.008 6.246 9.911.92 19.452-.49 26.839-2.019 11.573-2.449 10.773-13.166 6.586-15.124-33.915-15.797-26.47-9.368-33.24-14.573 17.235-20.39 43.213-41.577 53.369-110.222.8-5.448.121-8.877 0-13.287-.061-2.692.553-3.734 3.632-4.041 8.494-.981 16.742-3.305 24.314-7.471 21.975-12.002 30.84-31.719 32.933-55.355.307-3.612-.061-7.348-3.879-9.245v-.003zM249.4 351.89c-32.872-25.838-48.814-34.352-55.4-33.984-6.155.368-5.048 7.41-3.694 12.002 1.415 4.532 3.264 7.654 5.848 11.634 1.785 2.634 3.017 6.551-1.784 9.493-10.587 6.55-28.993-2.205-29.856-2.635-21.421-12.614-39.334-29.269-51.954-52.047-12.187-21.924-19.267-45.435-20.435-70.542-.308-6.061 1.478-8.207 7.509-9.307 7.94-1.471 16.127-1.778 24.068-.615 33.547 4.9 62.108 19.902 86.054 43.66 13.666 13.531 24.007 29.699 34.658 45.496 11.326 16.778 23.514 32.761 39.026 45.865 5.479 4.592 9.848 8.083 14.035 10.656-12.62 1.407-33.673 1.714-48.075-9.676zm15.899-102.519c.521-2.111 2.421-3.658 4.722-3.658a4.74 4.74 0 011.661.305c.678.246 1.293.614 1.786 1.163.861.859 1.354 2.083 1.354 3.368 0 2.695-2.154 4.837-4.862 4.837a4.748 4.748 0 01-4.738-4.034 5.01 5.01 0 01.077-1.981zm47.208 26.915c-2.606.996-5.2 1.778-7.707 1.88-4.679.244-9.787-1.654-12.556-3.981-4.308-3.612-7.386-5.631-8.679-11.941-.554-2.695-.247-6.858.246-9.246 1.108-5.144-.124-8.451-3.754-11.451-2.954-2.449-6.711-3.122-10.834-3.122-1.539 0-2.954-.673-4.001-1.224-1.724-.856-3.139-3-1.785-5.634.432-.856 2.525-2.939 3.018-3.305 5.6-3.185 12.065-2.144 18.034.244 5.54 2.266 9.727 6.429 15.759 12.307 6.155 7.102 7.263 9.063 10.773 14.39 2.771 4.163 5.294 8.451 7.018 13.348.877 2.561.071 4.74-2.341 6.277-.981.625-2.109 1.044-3.191 1.458z"/></svg>)
  },
  
];
  
  const dispatch = useDispatch();
  const { sendToWs } = useWebSocketContext();

  const handleChange = (id: number) => {
    const newSelectedModel = models.find(model => model.id === id)?.title;
    
    // Prevent selection of disabled models
    if (newSelectedModel && isModelDisabled(newSelectedModel)) {
      return;
    }
    
    if (newSelectedModel) {
      dispatch(setModel(newSelectedModel));

      // Prepare the message to be sent
      const msgToSend = JSON.stringify({
        method: 'set_model',
        params: {
          model_name: newSelectedModel,
        },
      });
      console.log('Sending message to server:', msgToSend);
      sendToWs(msgToSend);
    }

    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const groupedModels = Object.entries(
    models.reduce((acc: GroupedModels, model) => {
      if (!acc[model.provider]) {
        acc[model.provider] = [];
      }
      acc[model.provider].push(model);
      return acc;
    }, {})
  );
  
  //Setting background color for the button
  const backgroundMode = useSelector((state: RootState) => state.background.mode);


  return (
    <div 
    className={`absolute top-2 left-1 z-30 m-2 rounded-lg self-start hover:backdrop-blur-lg transition-all duration-100`}
    >
      <div className="relative w-full max-w-md" ref={dropdownRef}>
        {/* Dropdown Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full text-gray-300 pl-4 pr-2 py-2 rounded-lg transition-colors flex items-center gap-2 hover:bg-opacity-20"
          style={{
            backgroundColor:  backgroundMode === 'none'
        ? theme.palette.mode === 'dark'
          ? '#1E1E1E'
          : theme.palette.mode === 'light'
          ? '#F6F7FB'
          : theme.palette.mode === 'blue'
          ? '#CEE8FF'
          : 'transparent'
        : 'transparent',
          }}
        >
            <div className="w-6 h-6 rounded-full flex items-center justify-center">
              {models.find(model => model.title === selectedModel)?.icon}
            </div>
            <span className="text-md font-semibold flex items-center gap-1" style={{ color: theme.palette.text.secondary }}>
              {models.find(model => model.title === selectedModel)?.title || 'Select Model'}
              {isThinkingCapable(selectedModel) && (
                <span 
                  title="Thinking mode available" 
                  style={{ fontSize: '10px', opacity: 0.7 }}
                >
                  {MODEL_CAPABILITY_INDICATORS.THINKING}
                </span>
              )}
            </span>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={theme.palette.text.secondary}
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-0 w-[36rem] h-[22rem] overflow-y-scroll overflow-x-hidden mt-2 rounded-lg shadow-lg z-50 grid grid-cols-2 backdrop-blur-md bg-opacity-80"
          style={{ 
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(31, 31, 31, 0.7)' : 'rgba(255, 255, 255, 0.7)', 
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            scrollbarWidth: 'thin', 
            scrollbarColor: theme.palette.text.secondary + ' transparent', 
            boxShadow: theme.shadows[3] 
          }}
          >
            <div className="border-r border-gray-700">
              {groupedModels.slice(0, Math.ceil(groupedModels.length / 2)).map(([provider, providerModels]) => (
                <div key={provider} className="border-b border-gray-700 last:border-b-0">
                  <div className="px-3 py-2 text-xs font-semibold backdrop-blur-sm" style={{ color: alpha(theme.palette.text.secondary, 1), backgroundColor: alpha(theme.palette.text.secondary, 0.1) }}>
                    {provider}
                  </div>
                  {providerModels.map((model) => {
                    const disabled = isModelDisabled(model.title);
                    return (
                      <div 
                        className={`p-3 transition-colors duration-200 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${!disabled && (theme.palette.mode === 'dark' ? 'hover:bg-[#3e3e3e]' : 'hover:bg-gray-100')}`}
                        key={model.id} 
                        onClick={() => handleChange(model.id)}
                        style={{
                          filter: disabled ? 'blur(0.2px)' : 'none',
                          opacity: disabled ? 0.4 : 1,
                          pointerEvents: disabled ? 'none' : 'auto',
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center">
                              {model.icon}
                            </div>
                            <div>
                              <div className="text-sm font-medium flex items-center gap-1" style={{ color: theme.palette.text.secondary }}>
                                {model.title}
                                {isThinkingCapable(model.title) && (
                                  <span 
                                    title="Thinking mode available" 
                                    style={{ fontSize: '12px', opacity: 0.8 }}
                                  >
                                    {MODEL_CAPABILITY_INDICATORS.THINKING}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs"
                                style={{ color: theme.palette.text.disabled }}
                              >{model.desc}</div>
                            </div>
                          </div>
                          {selectedModel === model.title && (
                            <div className="pr-1">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill={theme.palette.text.secondary}
                                version="1.1"
                                id="Capa_1"
                                width="1.2rem"
                                height="1.2rem"
                                viewBox="0 0 40 40"
                              >
                                <g>
                                  <path d="M20,0C8.974,0,0,8.973,0,20c0,11.027,8.974,20,20,20c11.029,0,20-8.973,20-20C40,8.973,31.029,0,20,0z M28.818,17.875   l-8.562,8.564c-0.596,0.595-1.377,0.893-2.158,0.893c-0.779,0-1.561-0.298-2.156-0.893l-4.758-4.758   c-1.191-1.191-1.191-3.124,0-4.313c1.191-1.192,3.121-1.192,4.314,0l2.6,2.6l6.408-6.407c1.188-1.189,3.123-1.189,4.312,0   C30.01,14.752,30.01,16.684,28.818,17.875z" />
                                </g>
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div>
              {groupedModels.slice(Math.ceil(groupedModels.length / 2)).map(([provider, providerModels]) => (
                <div key={provider} className="border-b border-gray-700 last:border-b-0">
                  <div className="px-3 py-2 text-xs font-semibold backdrop-blur-sm" style={{ color: alpha(theme.palette.text.secondary, 1), backgroundColor: alpha(theme.palette.text.secondary, 0.1) }}>
                    {provider}
                  </div>
                  {providerModels.map((model) => {
                    const disabled = isModelDisabled(model.title);
                    return (
                      <div 
                        className={`p-3 transition-colors duration-200 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${!disabled && (theme.palette.mode === 'dark' ? 'hover:bg-[#3e3e3e]' : 'hover:bg-gray-100')}`}
                        key={model.id} 
                        onClick={() => handleChange(model.id)}
                        style={{
                          filter: disabled ? 'blur(0.2px)' : 'none',
                          opacity: disabled ? 0.4 : 1,
                          pointerEvents: disabled ? 'none' : 'auto',
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full flex items-center justify-center">
                              {model.icon}
                            </div>
                            <div>
                              <div className="text-sm font-medium flex items-center gap-1" style={{ color: theme.palette.text.secondary }}>
                                {model.title}
                                {isThinkingCapable(model.title) && (
                                  <span 
                                    title="Thinking mode available" 
                                    style={{ fontSize: '12px', opacity: 0.8 }}
                                  >
                                    {MODEL_CAPABILITY_INDICATORS.THINKING}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs"
                                style={{ color: theme.palette.text.disabled }}
                              >{model.desc}</div>
                            </div>
                          </div>
                          {selectedModel === model.title && (
                            <div className="pr-1">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill={theme.palette.text.secondary}
                                version="1.1"
                                id="Capa_1"
                                width="1.2rem"
                                height="1.2rem"
                                viewBox="0 0 40 40"
                              >
                                <g>
                                  <path d="M20,0C8.974,0,0,8.973,0,20c0,11.027,8.974,20,20,20c11.029,0,20-8.973,20-20C40,8.973,31.029,0,20,0z M28.818,17.875   l-8.562,8.564c-0.596,0.595-1.377,0.893-2.158,0.893c-0.779,0-1.561-0.298-2.156-0.893l-4.758-4.758   c-1.191-1.191-1.191-3.124,0-4.313c1.191-1.192,3.121-1.192,4.314,0l2.6,2.6l6.408-6.407c1.188-1.189,3.123-1.189,4.312,0   C30.01,14.752,30.01,16.684,28.818,17.875z" />
                                </g>
                              </svg>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Header;
